import React, { useCallback, useEffect, useState } from "react";
import {
  getCoachTeamPayments,
  getTeamPaymentDetail,
  formatMoney,
  type CoachTeamPayments,
  type TeamAthletePayments,
} from "../../services/payments";
import { useFeatures } from "../../hooks/useFeatures";

/**
 * Payment status, for a coach.
 *
 * A coach is not an accountant, and this is not a ledger. What they can act on
 * is "four families in this team are behind" and "these are which" — so the
 * team headline comes first and the names are one tap away.
 *
 * Amounts are collapsed by default. A coach reads this standing on a pitch
 * with parents and children around them, and "restant 250 RON" read over a
 * shoulder is a real problem for a real family.
 */
export default function CoachPaymentStatus() {
  const { features } = useFeatures();
  const [teams, setTeams] = useState<CoachTeamPayments[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [openTeam, setOpenTeam] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, TeamAthletePayments[]>>({});
  const [showAmounts, setShowAmounts] = useState(false);
  const [loadingTeam, setLoadingTeam] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setTeams(await getCoachTeamPayments());
    } catch {
      setTeams([]);
    }
    setLoaded(true);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (teamId: string) => {
    if (openTeam === teamId) {
      setOpenTeam(null);
      return;
    }
    setOpenTeam(teamId);
    if (!detail[teamId]) {
      setLoadingTeam(teamId);
      try {
        const data = await getTeamPaymentDetail(teamId);
        setDetail((prev) => ({ ...prev, [teamId]: data.athletes }));
      } catch {
        setDetail((prev) => ({ ...prev, [teamId]: [] }));
      }
      setLoadingTeam(null);
    }
  };

  if (!features.paymentsModule) return null;
  if (!loaded || teams.length === 0) return null;

  return (
    <div className="p-4 bg-white border border-slate-200 rounded-2xl shadow-[0_3px_0_#eef2f8]">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-extrabold text-slate-800">Plăți pe echipe</h3>
          <p className="mt-0.5 text-xs text-slate-500">Cine e la zi și cine a rămas în urmă.</p>
        </div>
        {/* Off by default — see the note at the top of this file. */}
        <button
          type="button"
          onClick={() => setShowAmounts((v) => !v)}
          className="px-2.5 py-1.5 text-[11px] font-bold rounded-lg bg-slate-100 text-slate-600"
        >
          {showAmounts ? "Ascunde sumele" : "Arată sumele"}
        </button>
      </div>

      <div className="mt-3 space-y-2">
        {teams.map((team) => {
          const isOpen = openTeam === team.team_id;
          const allSettled = team.owing === 0;
          return (
            <div key={team.team_id} className="overflow-hidden border border-slate-100 rounded-xl">
              <button
                type="button"
                onClick={() => toggle(team.team_id)}
                className="flex items-center w-full gap-3 px-3 py-3 text-left"
              >
                <span
                  className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                    team.overdue > 0 ? "bg-rose-500" : allSettled ? "bg-emerald-500" : "bg-amber-400"
                  }`}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-bold truncate text-slate-800">{team.team_name}</span>
                  <span className="block text-xs text-slate-500">
                    {allSettled
                      ? `${team.athletes} sportivi · toți la zi`
                      : `${team.settled} din ${team.athletes} la zi${
                          team.overdue > 0 ? ` · ${team.overdue} restanți` : ""
                        }`}
                  </span>
                </span>
                {showAmounts && team.outstanding_cents > 0 && (
                  <span className="text-xs font-extrabold tabular-nums text-slate-700">
                    {formatMoney(team.outstanding_cents)}
                  </span>
                )}
                <svg
                  className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isOpen && (
                <div className="px-3 pb-3 border-t border-slate-100">
                  {loadingTeam === team.team_id ? (
                    <p className="py-3 text-xs text-slate-400">Se încarcă...</p>
                  ) : (detail[team.team_id] || []).length === 0 ? (
                    <p className="py-3 text-xs text-slate-400">Nimic de arătat.</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {(detail[team.team_id] || [])
                        // Behind first: that is the only part worth a tap.
                        .slice()
                        .sort((a, b) => stateRank(a.state) - stateRank(b.state))
                        .map((a) => (
                          <div key={a.athlete_id} className="flex items-center gap-2 py-2.5">
                            <span
                              className={`w-2 h-2 rounded-full shrink-0 ${
                                a.state === "overdue"
                                  ? "bg-rose-500"
                                  : a.state === "owing"
                                    ? "bg-amber-400"
                                    : "bg-emerald-500"
                              }`}
                            />
                            <span className="flex-1 min-w-0">
                              <span className="block text-xs font-bold truncate text-slate-800">
                                {a.athlete_name || "Sportiv"}
                              </span>
                              {a.state !== "settled" && (
                                <span className="block text-[11px] text-slate-500">
                                  {a.periods.length}{" "}
                                  {a.periods.length === 1 ? "perioadă" : "perioade"}
                                  {a.state === "overdue" ? ` · restant de ${a.days_late} zile` : ""}
                                </span>
                              )}
                            </span>
                            {showAmounts && a.outstanding_cents > 0 && (
                              <span className="text-[11px] font-extrabold tabular-nums text-slate-700">
                                {formatMoney(a.outstanding_cents)}
                              </span>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
        Plata o face părintele din aplicație, sau o încasezi în numerar din ecranul Plăți.
      </p>
    </div>
  );
}

const stateRank = (s: TeamAthletePayments["state"]) =>
  s === "overdue" ? 0 : s === "owing" ? 1 : 2;
